// frontend/src/components/ShiftChangeRequestAdminBell.js
// 管理者向け: スタッフから送られた「シフト変更希望」を通知し、
// 一覧モーダルでステータス (unread/in_progress/done) を更新できる。
//
// Admin.js のヘッダー周辺にマウントする想定。
// RLS で org 内の shift_change_requests だけ読み書きできる。

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { MessageSquare, X, CheckCircle, Clock, Inbox, Loader2 } from 'lucide-react';
import { supabase } from '../supabase';
import { useOrganization } from '../contexts/OrganizationContext';

const POLL_INTERVAL_MS = 60 * 1000;

const STATUS_META = {
  unread: { label: '未読', color: '#dc2626', bg: '#fee2e2' },
  in_progress: { label: '対応中', color: '#2563eb', bg: '#dbeafe' },
  done: { label: '対応済', color: '#16a34a', bg: '#dcfce7' },
};

const STATUS_ORDER = ['unread', 'in_progress', 'done'];

const formatDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${y}/${m}/${day} ${hh}:${mm}`;
};

export default function ShiftChangeRequestAdminBell() {
  const { currentOrganization } = useOrganization();
  const [open, setOpen] = useState(false);
  const [requests, setRequests] = useState([]);
  const [staffMap, setStaffMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingId, setUpdatingId] = useState(null);
  const popupRef = useRef(null);

  const load = useCallback(async () => {
    if (!currentOrganization?.id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('shift_change_requests')
        .select('*')
        .eq('organization_id', currentOrganization.id)
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      setRequests(data || []);
      // 申請者の名前を staff から解決
      const empIds = Array.from(new Set((data || []).map(r => r.employee_id).filter(Boolean)));
      if (empIds.length > 0) {
        const { data: staffRows } = await supabase
          .from('staff')
          .select('employee_id, last_name, first_name')
          .eq('organization_id', currentOrganization.id)
          .in('employee_id', empIds);
        const map = {};
        (staffRows || []).forEach(s => {
          map[s.employee_id] = `${s.last_name || ''} ${s.first_name || ''}`.trim() || s.employee_id;
        });
        setStaffMap(map);
      }
    } catch (e) {
      console.error('shift_change_requests の取得に失敗:', e);
    } finally {
      setLoading(false);
    }
  }, [currentOrganization?.id]);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load]);

  // 外側クリックで閉じる
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const unreadCount = useMemo(
    () => requests.filter(r => r.status === 'unread').length,
    [requests]
  );

  const filteredRequests = useMemo(() => {
    if (statusFilter === 'all') return requests;
    return requests.filter(r => r.status === statusFilter);
  }, [requests, statusFilter]);

  const updateStatus = async (id, newStatus) => {
    setUpdatingId(id);
    try {
      const authData = await supabase.auth.getUser();
      const uid = authData?.data?.user?.id || null;
      const patch = { status: newStatus };
      if (newStatus === 'done' || newStatus === 'in_progress') {
        patch.handled_by_user_id = uid;
        patch.handled_at = new Date().toISOString();
      } else {
        patch.handled_by_user_id = null;
        patch.handled_at = null;
      }
      const { error } = await supabase
        .from('shift_change_requests')
        .update(patch)
        .eq('id', id)
        .eq('organization_id', currentOrganization.id);
      if (error) throw error;
      setRequests(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));
    } catch (e) {
      console.error('ステータス更新失敗:', e);
      alert('ステータス更新に失敗しました: ' + e.message);
    } finally {
      setUpdatingId(null);
    }
  };

  if (!currentOrganization?.id) return null;

  return (
    <div style={{ position: 'relative' }} ref={popupRef}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        title="シフト変更希望"
        style={styles.bellButton}
      >
        <MessageSquare size={20} />
        {unreadCount > 0 && (
          <span style={styles.badge}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div style={styles.popup}>
          <div style={styles.popupHeader}>
            <span style={styles.popupTitle}>
              <Inbox size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              シフト変更希望
            </span>
            <button
              onClick={load}
              style={styles.reloadBtn}
              title="再取得"
              disabled={loading}
            >
              {loading ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : '↻'}
            </button>
          </div>

          {/* ステータスフィルタ */}
          <div style={styles.filterBar}>
            {['all', 'unread', 'in_progress', 'done'].map((s) => {
              const label = s === 'all' ? 'すべて' : STATUS_META[s].label;
              const count = s === 'all' ? requests.length : requests.filter(r => r.status === s).length;
              const active = statusFilter === s;
              return (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  style={{
                    ...styles.filterChip,
                    background: active ? '#4338ca' : '#f3f4f6',
                    color: active ? '#fff' : '#374151',
                    fontWeight: active ? 700 : 500,
                  }}
                >
                  {label} ({count})
                </button>
              );
            })}
          </div>

          {filteredRequests.length === 0 ? (
            <div style={styles.empty}>
              {statusFilter === 'all' ? '変更希望はありません' : '該当する項目はありません'}
            </div>
          ) : (
            <div style={styles.list}>
              {filteredRequests.map((r) => {
                const meta = STATUS_META[r.status] || STATUS_META.unread;
                const staffName = staffMap[r.employee_id] || `(社員番号 ${r.employee_id})`;
                const dateLabel = r.shift_date ? `${r.year_month} / ${r.shift_date}` : `${r.year_month} 全体`;
                return (
                  <div key={r.id} style={styles.item}>
                    <div style={styles.itemHead}>
                      <span style={{ ...styles.statusBadge, background: meta.bg, color: meta.color }}>
                        {meta.label}
                      </span>
                      <span style={styles.staffName}>{staffName}</span>
                      <span style={styles.dateLabel}>{dateLabel}</span>
                    </div>
                    {(r.current_selection || r.requested_selection) && (
                      <div style={styles.selectionRow}>
                        {r.current_selection && (
                          <span style={styles.selectionTag}>
                            現在: <strong>{r.current_selection}</strong>
                          </span>
                        )}
                        {r.requested_selection && (
                          <span style={styles.selectionTag}>
                            希望: <strong>{r.requested_selection}</strong>
                          </span>
                        )}
                      </div>
                    )}
                    {r.note && <div style={styles.note}>{r.note}</div>}
                    <div style={styles.itemFoot}>
                      <span style={styles.timestamp}>
                        <Clock size={11} style={{ verticalAlign: 'middle', marginRight: 2 }} />
                        {formatDateTime(r.created_at)}
                      </span>
                      <div style={styles.actionGroup}>
                        {STATUS_ORDER.filter(s => s !== r.status).map((s) => {
                          const meta2 = STATUS_META[s];
                          return (
                            <button
                              key={s}
                              onClick={() => updateStatus(r.id, s)}
                              disabled={updatingId === r.id}
                              style={{
                                ...styles.actionBtn,
                                borderColor: meta2.color,
                                color: meta2.color,
                              }}
                              title={`${meta2.label}にする`}
                            >
                              {s === 'done' && <CheckCircle size={11} style={{ marginRight: 2 }} />}
                              {meta2.label}へ
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={styles.popupFooter}>
            <button onClick={() => setOpen(false)} style={styles.closeBtn}>
              <X size={14} />閉じる
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  bellButton: {
    position: 'relative',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    color: '#374151',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  badge: {
    position: 'absolute',
    top: 2,
    right: 2,
    background: '#f59e0b',
    color: '#fff',
    fontSize: '0.7rem',
    fontWeight: 'bold',
    padding: '1px 5px',
    borderRadius: '9px',
    minWidth: '16px',
    textAlign: 'center',
    lineHeight: '14px',
  },
  popup: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 8,
    width: 'min(460px, 95vw)',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
    zIndex: 10001,
    maxHeight: '75vh',
    display: 'flex',
    flexDirection: 'column',
  },
  popupHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid #f3f4f6',
  },
  popupTitle: { fontWeight: 700, fontSize: '0.95rem', color: '#111827' },
  reloadBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1rem',
    color: '#6b7280',
  },
  filterBar: {
    display: 'flex',
    gap: 6,
    padding: '8px 12px',
    borderBottom: '1px solid #f3f4f6',
    flexWrap: 'wrap',
  },
  filterChip: {
    padding: '4px 10px',
    borderRadius: 999,
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.75rem',
  },
  empty: {
    padding: '2rem 1rem',
    textAlign: 'center',
    color: '#9ca3af',
    fontSize: '0.9rem',
  },
  list: {
    overflowY: 'auto',
    flex: 1,
    padding: '4px 0',
  },
  item: {
    padding: '10px 16px',
    borderBottom: '1px solid #f3f4f6',
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  itemHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  statusBadge: {
    fontSize: '0.7rem',
    padding: '2px 8px',
    borderRadius: 999,
    fontWeight: 700,
  },
  staffName: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#111827',
  },
  dateLabel: {
    fontSize: '0.8rem',
    color: '#4b5563',
    marginLeft: 'auto',
  },
  selectionRow: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  selectionTag: {
    fontSize: '0.75rem',
    background: '#f3f4f6',
    padding: '2px 8px',
    borderRadius: 6,
    color: '#374151',
  },
  note: {
    fontSize: '0.85rem',
    color: '#374151',
    background: '#fafafa',
    padding: '6px 10px',
    borderRadius: 6,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  itemFoot: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  timestamp: {
    fontSize: '0.7rem',
    color: '#9ca3af',
  },
  actionGroup: {
    display: 'flex',
    gap: 4,
  },
  actionBtn: {
    fontSize: '0.7rem',
    padding: '3px 8px',
    borderRadius: 6,
    border: '1px solid',
    background: '#fff',
    cursor: 'pointer',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
  },
  popupFooter: {
    padding: '8px 12px',
    borderTop: '1px solid #f3f4f6',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  closeBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '4px 10px',
    fontSize: '0.75rem',
    background: '#f3f4f6',
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
    color: '#374151',
  },
};
